// hooks/useRedemption.js
// BuzzInApp — Lógica de canje completa
//
// Orquesta todas las capas de seguridad en el momento del canje:
//   Capa 3 — Verifica el QR (firma + expiración)
//   Capa 4 — Geofencing (distancia + GPS)
//   Capa 5 — Mock location
//   Capa 6 — Rate limiting (1 canje / usuario / comercio / 30 días)
//   Capa 7 — Oferta snapshotada
//   Capa 8 — Trust score
//   Capa 9 — Flag cuenta nueva para revisión manual

import { useState, useCallback } from "react";
import { collection } from "firebase/firestore";
import {
  db,
  getDocument,
  updateDocument,
  addDocument,
  getDocs,
  query,
  where,
  serverTimestamp,
  increment,
  COLLECTIONS,
  Timestamp,
} from "../config/firebase";
import {
  RATE_LIMITING,
  OFFER_SNAPSHOT,
  TRUST_SCORE,
} from "../config/security";
import { verifyQRPayload } from "../security/qrGenerator";
import { applyTrustDelta, isNewAccount } from "../security/fraudDetector";

// ─── Estados del proceso de canje ─────────────────────────────────────────────
export const REDEMPTION_STATUS = {
  IDLE:      "idle",
  VERIFYING: "verifying",
  SUCCESS:   "success",
  FAILED:    "failed",
};

// ─── Razones de fallo (mapeadas a claves i18n en claim.*) ─────────────────────
export const REDEMPTION_ERRORS = {
  QR_EXPIRED:         "qrExpired",
  QR_INVALID:         "qrInvalid",
  TOO_FAR:            "tooFarAway",
  MOCK_LOCATION:      "mockLocation",
  NOT_ENOUGH_CLICKS:  "notEnoughClicks",
  ALREADY_CLAIMED:    "alreadyClaimed",
  RATE_LIMITED:       "rateLimited",
  PHONE_NOT_VERIFIED: "smsRequired",
  SERVER_ERROR:       "serverError",
  NETWORK_ERROR:      "networkError",
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
const useRedemption = ({ user, profile, geoResult, clickCount, linkId }) => {
  const [status, setStatus]         = useState(REDEMPTION_STATUS.IDLE);
  const [error, setError]           = useState(null);
  const [errorData, setErrorData]   = useState({});
  const [redemption, setRedemption] = useState(null);

  // ─── Helper interno para fallos ─────────────────────────────────────────
  const fail = useCallback((errorKey, data = {}) => {
    setStatus(REDEMPTION_STATUS.FAILED);
    setError(errorKey);
    setErrorData(data);
    return { success: false, error: errorKey, data };
  }, []);

  /**
   * Ejecuta el canje completo dado el string raw del QR escaneado.
   * @param {string} qrString  String leído por qrScanner.js
   * @returns {Promise<{ success: boolean, redemptionId?: string, offer?: object }>}
   */
  const redeem = useCallback(async (qrString) => {
    setStatus(REDEMPTION_STATUS.VERIFYING);
    setError(null);
    setErrorData({});

    try {
      // ── 1. Verificar teléfono ─────────────────────────────────────────
      if (!profile?.phoneVerified) {
        return fail(REDEMPTION_ERRORS.PHONE_NOT_VERIFIED);
      }

      // ── 2. Verificar QR (firma + expiración) ──────────────────────────
      const qrResult = await verifyQRPayload(qrString);
      if (!qrResult.valid) {
        const errKey =
          qrResult.reason === "expired"
            ? REDEMPTION_ERRORS.QR_EXPIRED
            : REDEMPTION_ERRORS.QR_INVALID;
        return fail(errKey);
      }

      const { bid: businessId } = qrResult.payload;

      // ── 3. Geofencing ─────────────────────────────────────────────────
      if (geoResult) {
        if (geoResult.reason === "too_far") {
          return fail(REDEMPTION_ERRORS.TOO_FAR, {
            distance: geoResult.distanceMeters,
          });
        }
        if (geoResult.reason === "mock_location_detected") {
          return fail(REDEMPTION_ERRORS.MOCK_LOCATION);
        }
      }

      // ── 4. Verificar clicks suficientes ───────────────────────────────
      const link   = await getDocument("links", linkId);
      const offer  = link?.[OFFER_SNAPSHOT.SNAPSHOT_KEY] ?? {};
      const needed = offer.prize_clicks_needed ?? 1;

      if (clickCount < needed) {
        return fail(REDEMPTION_ERRORS.NOT_ENOUGH_CLICKS, {
          needed,
          current: clickCount,
        });
      }

      // ── 5. Rate limiting: 1 canje / usuario / comercio / 30 días ─────
      const cooldownCheck = await checkCooldown(user.uid, businessId);
      if (!cooldownCheck.allowed) {
        return fail(REDEMPTION_ERRORS.ALREADY_CLAIMED, {
          days: cooldownCheck.daysRemaining,
        });
      }

      // ── 6. Rate limiting diario ───────────────────────────────────────
      const dailyCheck = await checkDailyLimit(user.uid);
      if (!dailyCheck.allowed) {
        return fail(REDEMPTION_ERRORS.RATE_LIMITED);
      }

      // ── 7. Registrar canje en Firestore ───────────────────────────────
      const newAccountFlag = isNewAccount(
        profile?.createdAt?.toDate?.() ?? new Date()
      );

      const redemptionData = {
        uid:            user.uid,
        businessId,
        linkId,
        clickCount,
        offer,
        geoTier:        geoResult?.tier          ?? "unavailable",
        gpsAbsent:      geoResult?.gpsAbsent      ?? true,
        mockDetected:   geoResult?.mockDetected    ?? false,
        isNewAccount:   newAccountFlag,
        requiresReview: newAccountFlag,            // revisión manual para cuentas nuevas
        status:         "confirmed",
        createdAt:      serverTimestamp(),
      };

      const redemptionId = await addDocument(
        COLLECTIONS.REDEMPTIONS,
        redemptionData
      );

      // ── 8. Actualizar contadores del usuario ──────────────────────────
      await updateDocument(COLLECTIONS.USERS, user.uid, {
        totalRedemptions: increment(1),
        updatedAt:        serverTimestamp(),
      });

      // ── 9. Trust score: +5 por canje legítimo ─────────────────────────
      await applyTrustDelta(
        user.uid,
        TRUST_SCORE.DELTA.LEGITIMATE_REDEMPTION,
        "legitimate_redemption"
      );

      const result = { redemptionId, offer, businessId };
      setRedemption(result);
      setStatus(REDEMPTION_STATUS.SUCCESS);
      return { success: true, ...result };

    } catch (err) {
      console.error("[useRedemption] error:", err);
      const isNetwork = !navigator.onLine || err.code === "unavailable";
      return fail(
        isNetwork
          ? REDEMPTION_ERRORS.NETWORK_ERROR
          : REDEMPTION_ERRORS.SERVER_ERROR
      );
    }
  }, [user, profile, geoResult, clickCount, linkId, fail]);

  const reset = useCallback(() => {
    setStatus(REDEMPTION_STATUS.IDLE);
    setError(null);
    setErrorData({});
    setRedemption(null);
  }, []);

  return {
    status,
    error,
    errorData,
    redemption,
    isVerifying: status === REDEMPTION_STATUS.VERIFYING,
    isSuccess:   status === REDEMPTION_STATUS.SUCCESS,
    isFailed:    status === REDEMPTION_STATUS.FAILED,
    redeem,
    reset,
  };
};

// ─── Helpers de rate limiting ─────────────────────────────────────────────────
// Viven fuera del hook para no recrearse en cada render.
// Usan imports estáticos del top del archivo — sin imports dinámicos.

/**
 * Verifica si el usuario puede canjear en ese comercio (cooldown 30 días).
 * @param {string} uid
 * @param {string} businessId
 * @returns {Promise<{ allowed: boolean, daysRemaining?: number }>}
 */
const checkCooldown = async (uid, businessId) => {
  const since = new Date();
  since.setDate(since.getDate() - RATE_LIMITING.REDEMPTION_COOLDOWN_DAYS);

  const ref  = collection(db, COLLECTIONS.REDEMPTIONS);
  const snap = await getDocs(
    query(
      ref,
      where("uid",        "==", uid),
      where("businessId", "==", businessId),
      where("createdAt",  ">=", Timestamp.fromDate(since))
    )
  );

  if (snap.empty) return { allowed: true };

  // Calcular días restantes del cooldown más reciente
  const lastRedemption = snap.docs
    .map((d) => d.data().createdAt?.toDate?.() ?? new Date(0))
    .sort((a, b) => b - a)[0];

  const msSinceLast   = Date.now() - lastRedemption.getTime();
  const msRemaining   =
    RATE_LIMITING.REDEMPTION_COOLDOWN_DAYS * 86_400_000 - msSinceLast;
  const daysRemaining = Math.ceil(msRemaining / 86_400_000);

  return { allowed: false, daysRemaining };
};

/**
 * Verifica si el usuario no superó el límite diario de canjes.
 * @param {string} uid
 * @returns {Promise<{ allowed: boolean }>}
 */
const checkDailyLimit = async (uid) => {
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);

  const ref  = collection(db, COLLECTIONS.REDEMPTIONS);
  const snap = await getDocs(
    query(
      ref,
      where("uid",       "==", uid),
      where("createdAt", ">=", Timestamp.fromDate(startOfDay))
    )
  );

  return { allowed: snap.size < RATE_LIMITING.MAX_REDEMPTIONS_PER_DAY };
};

export default useRedemption;
